import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {lastValueFrom, Subject} from "rxjs";
import {global} from "./global";
import {AuthService} from "./auth.service";

/**
 * Estado de la conexión con el servidor, para poder avisar al xofer con una etiqueta fija en vez
 * de echarlo al login o dejarlo con un aviso vacío.
 *
 * No basta con navigator.onLine: en el móvil sigue diciendo que hay conexión aunque no haya
 * cobertura real, y tampoco se entera de que el servidor esté caído. Lo que manda es si una
 * petición llega al servidor (status 0 o 502-504 = no llega); cualquier respuesta suya, aunque sea
 * un error, significa que se le alcanza.
 *
 * Para que el aviso salga sin esperar a que el xofer toque algo, se le da un toque al servidor cada
 * pocos segundos: seguido mientras no hay conexión (para recuperarla cuanto antes y avisar por
 * `restored`), y más espaciado mientras la hay. Con la app en segundo plano no se toca nada.
 */
@Injectable({
    providedIn: 'root'
})
export class ConnectionService {

    /** Cada cuánto se tantea al servidor, según haya conexión o no */
    private static readonly ONLINE_SECONDS = 30;
    private static readonly OFFLINE_SECONDS = 10;

    offline: boolean = false;

    /** Se emite cuando el servidor vuelve a responder tras haber estado sin conexión. */
    restored = new Subject<void>();

    private timer: any = null;

    constructor(
        private http: HttpClient,
        private auth: AuthService,
    ) {
        if (typeof window === 'undefined') return;

        // El evento del navegador no garantiza que se alcance el servidor: solo es una pista para
        // tantearlo ya, sin esperar al siguiente toque.
        window.addEventListener('online', () => this.probe());
        window.addEventListener('offline', () => this.setOffline());

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') this.probe();
        });

        if (navigator.onLine === false) {
            this.offline = true;
        }

        this.schedule();
    }

    /** Si el error de una petición es por no alcanzar el servidor (y no una respuesta suya). */
    isOfflineError(error: any): boolean {
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;

        const status = error?.status ?? 0;

        return status === 0 || status === 502 || status === 503 || status === 504;
    }

    /** Una petición ha fallado: marca "sin conexión" si el motivo es de red. Devuelve si lo es. */
    reportError(error: any): boolean {
        const offline = this.isOfflineError(error);

        if (offline) {
            this.setOffline();
        }

        return offline;
    }

    /** Una petición ha llegado al servidor: hay conexión. */
    reportSuccess(): void {
        this.setOnline();
    }

    private setOffline(): void {
        const wasOnline = !this.offline;

        this.offline = true;

        if (wasOnline) {
            this.schedule();
        }
    }

    private setOnline(): void {
        const wasOffline = this.offline;

        this.offline = false;

        if (wasOffline) {
            this.schedule();
            // Solo se avisa en el momento de recuperarla, no en cada petición que va bien
            this.restored.next();
        }
    }

    private schedule(): void {
        if (this.timer !== null) {
            clearInterval(this.timer);
        }

        const seconds = this.offline ? ConnectionService.OFFLINE_SECONDS : ConnectionService.ONLINE_SECONDS;

        this.timer = setInterval(() => this.probe(), seconds * 1000);
    }

    /** Toca el servidor con una petición pequeña; cualquier respuesta suya vale como "hay red". */
    private async probe(): Promise<void> {
        if (!this.auth.check()) return;
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;

        const headers = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded')
            .set('Authorization', this.auth.getToken());

        try {
            await lastValueFrom(this.http.get(global.getUrl() + 'xofers/getnomxofer', {headers: headers}));
            this.setOnline();
        } catch (error) {
            if (this.isOfflineError(error)) {
                this.setOffline();
            } else {
                this.setOnline();
            }
        }
    }
}
